import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ClipboardCheck, Clock, Trophy, ArrowRight, Target, TrendingUp, TrendingDown, Minus, Sparkles, Calendar, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { PageContainer } from '../../components/shared/PageContainer';
import { PageHero, HeroStatPill } from '../../components/shared/PageHero';
import { itemVariants } from '../../components/shared/animations';
import { Skeleton } from '../../components/shared/Skeleton';
import { DjayAvatar } from '../../mascot';
import { useDjaySelection } from '../../mascot';
import { GroupId } from '../../config/systemConfig';
import { error as logError } from '../../utils/logger';
import { SectionHeader } from '../../components/ui/SectionHeader';
import { PRIMARY } from '../../config/designTokens';
import { AssessmentConfig, AssessmentDomainId, AssessmentHistoryEntry, AssessmentReport, SubTestDefinition, TeacherAssignment } from '../types';
import type { CooldownResult } from '../types';
import { getAssessmentHistoryForLearner, checkDomainCooldownForLearner, getReport } from '../utils/assessmentStorage';
import { getAvailableSubTests, DOMAIN_META, DOMAIN_MODULE_MAP } from '../utils/assessmentDomainConfig';
import { dataLayer } from '../../data/dataLayer';
const DEFAULT_QUESTION_COUNT = 30;
const URGENT_DEADLINE_MS = 2 * 24 * 60 * 60 * 1000;
interface AssessmentPageProps {
  selectedGroup: GroupId;
  onStart: (config: AssessmentConfig) => void;
  onViewReport: (report: AssessmentReport) => void;
  /**
   * AS-2026-07-P1-01: identifies WHOSE assessments this page shows. Without
   * it we cannot attribute history or cooldowns, so the page renders an
   * explicit "no learner selected" state rather than falling back to
   * group-wide data (which would leak classmates' results).
   */
  learnerId?: string;
}
export function AssessmentPage({
  selectedGroup,
  onStart,
  onViewReport,
  learnerId
}: AssessmentPageProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  const [history, setHistory] = useState<AssessmentHistoryEntry[]>([]);
  const [reports, setReports] = useState<Record<string, AssessmentReport>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [showAllResults, setShowAllResults] = useState(false);
  const [assignments, setAssignments] = useState<TeacherAssignment[]>([]);
  const [domainCooldowns, setDomainCooldowns] = useState<Record<AssessmentDomainId, CooldownResult>>({
    rekenen: {
      canStart: true,
      remainingMs: 0,
      lastCompletedAt: 0
    },
    taalverzorging: {
      canStart: true,
      remainingMs: 0,
      lastCompletedAt: 0
    },
    'begrijpend-lezen': {
      canStart: true,
      remainingMs: 0,
      lastCompletedAt: 0
    }
  });
  const [loadError, setLoadError] = useState(false);
  useEffect(() => {
    let isMounted = true;
    // AS-2026-07-P1-01: never fall back to group-wide reads. Without a
    // learner we cannot know whose results these are, so we show nothing.
    if (!learnerId) {
      setHistory([]);
      setReports({});
      setAssignments([]);
      setLoadError(false);
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }
    // Capture as a local const so the scope is unambiguous inside the async
    // closures below (and so TS keeps the non-nullable narrowing).
    const activeLearnerId: string = learnerId;
    const loadData = async () => {
      setIsLoading(true);
      setLoadError(false);
      let failed = false;
      try {
        const h = await getAssessmentHistoryForLearner(activeLearnerId, selectedGroup);
        if (isMounted) setHistory(h);
        const loadedReports: Record<string, AssessmentReport> = {};
        await Promise.all(h.map(async entry => {
          const report = await getReport(entry.attemptId);
          if (report) loadedReports[entry.attemptId] = report;
        }));
        if (isMounted) setReports(loadedReports);
        const assignmentsData = await dataLayer.getTeacherAssignments();
        const allAssignments = Object.values(assignmentsData);
        const myAssignments = allAssignments.filter(a => {
          if (a.status !== 'active') return false;
          if (a.deadline && Date.now() > a.deadline) return false;
          const completedBy = a.completedByStudentIds || [];
          if (completedBy.includes(activeLearnerId)) return false;
          if (a.wholeClass && a.group === selectedGroup) return true;
          if (a.targetStudentIds && a.targetStudentIds.includes(activeLearnerId)) return true;
          return false;
        });
        if (isMounted) setAssignments(myAssignments.sort((a, b) => b.createdAt - a.createdAt));
      } catch (e) {
        failed = true;
        logError('Failed to load assessment history', e);
      }
      try {
        const domains: AssessmentDomainId[] = ['rekenen', 'taalverzorging', 'begrijpend-lezen'];
        const cooldowns: Record<string, CooldownResult> = {};
        await Promise.all(domains.map(async domainId => {
          cooldowns[domainId] = await checkDomainCooldownForLearner(activeLearnerId, selectedGroup, domainId);
        }));
        if (isMounted) setDomainCooldowns(cooldowns as Record<AssessmentDomainId, CooldownResult>);
      } catch (e) {
        failed = true;
        logError('Failed to check cooldowns', e);
      }
      if (isMounted) {
        setLoadError(failed);
        setIsLoading(false);
      }
    };
    loadData();
    return () => {
      isMounted = false;
    };
  }, [selectedGroup, learnerId]);
  const handleStartSubTest = (subTest: SubTestDefinition) => {
    onStart({
      questionCount: subTest.questionCount,
      moduleFilter: subTest.modules,
      minModules: 1,
      countsForMastery: false,
      seed: Date.now(),
      domainId: subTest.domainId
    });
  };
  const handleStartAssignment = (assignment: TeacherAssignment) => {
    const modules = DOMAIN_MODULE_MAP[assignment.domainId as keyof typeof DOMAIN_MODULE_MAP] || null;
    onStart({
      domainId: assignment.domainId,
      mode: assignment.type === 'targeted' ? 'targeted' : 'general',
      targetFamilyIds: assignment.selectedFamilyIds || [],
      assignmentId: assignment.id,
      questionCount: assignment.questionCount || DEFAULT_QUESTION_COUNT,
      moduleFilter: modules,
      minModules: 1,
      countsForMastery: false,
      seed: Date.now()
    });
  };
  const hasHistory = history.length > 0;
  const bestScore = hasHistory ? Math.max(...history.map(h => h.score)) : null;
  const availableSubTests = getAvailableSubTests(selectedGroup);
  const displayedHistory = showAllResults ? history : history.slice(0, 5);
  const anyDomainOnCooldown = Object.values(domainCooldowns).some(c => !c.canStart);
  const maxRemainingMs = Math.max(...Object.values(domainCooldowns).map(c => c.remainingMs));
  return <PageContainer className="space-y-5 sm:space-y-6 pb-20" data-id="element-702" data-mp="50xffwxj2f5d">
      {/* Hero */}
      <motion.div variants={itemVariants} data-id="element-703" data-mp="3dentutdfm71c">
        <PageHero title="Toetsen" subtitle={hasHistory ? 'Laat zien hoeveel je gegroeid bent!' : 'Ontdek waar je staat met een toets!'} gradientClasses={PRIMARY.gradient} djayEmotion={hasHistory ? 'excited' : 'encouraging'} djayVariant={selectedVariant} icon={ClipboardCheck} iconBgClass="bg-orange-400" floatingEmojis={['📝', '⭐']} data-id="element-704" data-mp="3ddctz4n3xjfi">
          <HeroStatPill icon={Target} data-id="element-705" data-mp="2ppbqia4sct6y">Groep {selectedGroup}</HeroStatPill>
          {hasHistory && <HeroStatPill icon={ClipboardCheck} data-id="element-706" data-mp="255m6eufh12f6">
              {history.length} toets{history.length !== 1 ? 'en' : ''} gemaakt
            </HeroStatPill>}
          {bestScore !== null && <HeroStatPill icon={Trophy} data-id="element-707" data-mp="2e34wwebvw8yg">Topscore: {bestScore}%</HeroStatPill>}
        </PageHero>
      </motion.div>

      {/* Teacher-Assigned Assessments */}
      {assignments.length > 0 && <motion.div variants={itemVariants} data-id="element-708" data-mp="2o2ndcksl5clr">
          <Card className="p-6 sm:p-8" data-id="element-709" data-mp="2s3z4iur3y5w2">
            <SectionHeader title="Van je Leerkracht" icon={Sparkles} accentColor="bg-indigo-100 text-indigo-600" description="Je leerkracht heeft een toets voor je klaargezet. Doe rustig je best!" className="mb-6" data-id="element-710" data-mp="2s2xrwlug1bc8" />
            <div className="space-y-3" data-id="element-711" data-mp="36uy9lvsqicaw">
              {assignments.map(assignment => {
            const meta = DOMAIN_META[assignment.domainId as keyof typeof DOMAIN_META];
            const Icon = meta?.icon;
            const hasDeadline = assignment.deadline;
            const isUrgent = hasDeadline && assignment.deadline - Date.now() < URGENT_DEADLINE_MS;
            return <button key={assignment.id} onClick={() => handleStartAssignment(assignment)} className={`w-full text-left rounded-2xl p-4 border-2 transition-all hover:shadow-md flex items-center gap-4 ${isUrgent ? 'border-orange-300 bg-orange-50/50' : 'border-white bg-white hover:border-indigo-200'}`} data-id="element-712" data-mp="l8dm24suh7o6">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${meta?.bgColor || 'bg-slate-100'} ${meta?.color || 'text-slate-500'}`} data-id="element-713" data-mp="146zobn03l4to">
                      {Icon ? <Icon className="w-6 h-6" data-id="element-714" data-mp="1wu2kw9l7r5ha" /> : <ClipboardCheck className="w-6 h-6" data-id="element-715" data-mp="3qafm2azmdznk" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-716" data-mp="3m9ncfgmu5sqr">
                      <div className="flex items-center gap-2 flex-wrap" data-id="element-717" data-mp="4267l9r8vgcg">
                        <h3 className="font-black text-base text-slate-900" data-id="element-718" data-mp="403c4a7bov0c">
                          {assignment.domainLabel}
                        </h3>
                        {assignment.type === 'targeted' && <Badge variant="default" className="bg-purple-100 text-purple-700 border-purple-200 text-[10px]" data-id="element-719" data-mp="91slzmit72mu">
                            Gericht
                          </Badge>}
                        {isUrgent && <Badge variant="default" className="bg-orange-100 text-orange-700 border-orange-300 text-[10px] animate-pulse" data-id="element-720" data-mp="wrwofqk9gwc9">
                            Bijna deadline!
                          </Badge>}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 font-medium" data-id="element-721" data-mp="11fwgyuo5n9h6">
                        <span className="flex items-center gap-1" data-id="element-722" data-mp="16eh7g2dluesf">
                          <Clock className="w-3 h-3" data-id="element-723" data-mp="16gtx71plttg1" />
                          ~15 min
                        </span>
                        {hasDeadline && <span className={`flex items-center gap-1 ${isUrgent ? 'text-orange-600 font-bold' : ''}`} data-id="element-724" data-mp="whctuqxtrrn8">
                            <Calendar className="w-3 h-3" data-id="element-725" data-mp="nnq2dd0nhaju" />
                            {new Date(assignment.deadline).toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'short'
                    })}
                          </span>}
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-indigo-400 flex-shrink-0" data-id="element-726" data-mp="3h119tkqd3p7w" />
                  </button>;
          })}
            </div>
          </Card>
        </motion.div>}

      {/* Niveaubepaling */}
      <motion.div variants={itemVariants} data-id="element-727" data-mp="dkkms9heqj2e">
        <Card className="p-6 sm:p-8" data-id="element-728" data-mp="di89i9mlgwvo">
          <SectionHeader title="Ontdek je Niveau" icon={Target} accentColor="bg-orange-100 text-orange-600" description="Kies een toets om te laten zien wat je al kan! Geen stress, gewoon je best doen." className="mb-6" data-id="element-729" data-mp="8komak7x59rl" />
          {isLoading ? <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-730" data-mp="2odf5295eeejg">
              {[1, 2, 3].map(i => <div key={i} className="bg-white/50 rounded-2xl p-4 border-2 border-white" data-id="element-731" data-mp="2jchq05foq0m6">
                  <Skeleton className="w-10 h-10 rounded-xl mb-3" data-id="element-732" data-mp="13ubd7a1dvowz" />
                  <Skeleton className="h-5 w-3/4 mb-2" data-id="element-733" data-mp="13uuz11u5mxga" />
                  <Skeleton className="h-4 w-full mb-3" data-id="element-734" data-mp="18swe7bogmcmh" />
                  <Skeleton className="h-3 w-20" data-id="element-735" data-mp="18rbkfo9yyxpo" />
                </div>)}
            </div> : <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-736" data-mp="1ij7rvpxy406k">
              {availableSubTests.map((subTest, i) => {
            const meta = DOMAIN_META[subTest.domainId];
            const cooldown = domainCooldowns[subTest.domainId];
            const canStartThis = cooldown?.canStart !== false && !isLoading;
            return <motion.div key={subTest.domainId} initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.08
            }} data-id="element-737" data-mp="1wih1u7vk5y23" data-mpi={i}>
                    <button onClick={() => canStartThis && handleStartSubTest(subTest)} disabled={!canStartThis} className={`w-full text-left rounded-2xl p-4 border-2 transition-all flex flex-col h-full ${!canStartThis ? 'opacity-50 cursor-not-allowed border-slate-200 bg-slate-50' : `bg-white hover:shadow-lg hover:-translate-y-0.5 ${meta.borderColor}`}`} data-id="element-738" data-mp="1npcpecacoia7">
                      <div className="flex items-center gap-3 mb-3" data-id="element-739" data-mp="1iq059zav5pbb">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${meta.bgColor} ${meta.color}`} data-id="element-740" data-mp="1dwdr22p1xdde">
                          <meta.icon className="w-5 h-5" data-id="element-741" data-mp="m6solkqxzeq7" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-742" data-mp="c1j1zqei7849">
                          <h3 className={`font-black text-base ${meta.color}`} data-id="element-743" data-mp="bzzm40wrez30">
                            {subTest.label}
                          </h3>
                        </div>
                      </div>
                      <p className="text-xs text-slate-600 font-medium mb-3 flex-grow leading-relaxed" data-id="element-744" data-mp="2221dne3zkf5">
                        {meta.description}
                      </p>
                      <div className="flex items-center gap-3 text-[11px] text-slate-500 font-medium" data-id="element-745" data-mp="utbuzktkotbz">
                        <span className="flex items-center gap-1" data-id="element-746" data-mp="zqvjddjzimhn">
                          <ClipboardCheck className="w-3 h-3" data-id="element-747" data-mp="ktcorukmlf2a" />
                          {subTest.questionCount}v
                        </span>
                        <span className="flex items-center gap-1" data-id="element-748" data-mp="pvacp23z0y1k">
                          <Clock className="w-3 h-3" data-id="element-749" data-mp="35j20hyq18kqr" />~
                          {subTest.estimatedMinutes}m
                        </span>
                        {subTest.shortNote && <span className={`px-1.5 py-0.5 rounded ${meta.bgColor} ${meta.color} font-bold`} data-id="element-750" data-mp="1gzo31o2516py">
                            {subTest.shortNote}
                          </span>}
                      </div>
                      {cooldown && !cooldown.canStart && <div className="mt-2 text-[11px] font-bold px-2 py-1 rounded-md bg-slate-100 text-slate-500 inline-block" data-id="element-751" data-mp="xcqf1exycai3">
                          ⏳ Nog {formatRemainingTime(cooldown.remainingMs)}
                        </div>}
                    </button>
                  </motion.div>;
          })}
            </div>}

          {anyDomainOnCooldown && <div className="mt-4 bg-white/60 border border-white rounded-xl p-3 flex items-center gap-3 text-sm text-slate-600 font-medium" data-id="element-752" data-mp="2iquqs2mrg6p8">
              <span className="text-lg" data-id="element-753" data-mp="2ds1egdj7mlqn">⏳</span>
              <span data-id="element-754" data-mp="2lbapeb7d6sht">
                Je hebt recent een toets gemaakt. Nog{' '}
                <strong data-id="element-755" data-mp="2q9ucwg1lwcca">{formatRemainingTime(maxRemainingMs)}</strong> wachten.
              </span>
            </div>}
        </Card>
      </motion.div>

      {/* Results */}
      <motion.div variants={itemVariants} data-id="element-756" data-mp="2v2neww5ox6ko">
        <Card className="p-6 sm:p-8" data-id="element-757" data-mp="2zzx368hcfc6x">
          <SectionHeader title="Jouw Resultaten" icon={Trophy} accentColor="bg-amber-100 text-amber-600" description={hasHistory ? 'Kijk eens hoe goed je het hebt gedaan!' : 'Na je eerste toets zie je hier je resultaten!'} className="mb-6" data-id="element-758" data-mp="2zyvrc1si6lo5" />
          {isLoading ? <div className="space-y-2" data-id="element-759" data-mp="2hah1hhtx9dox">
              {[1, 2, 3].map(i => <div key={i} className="bg-white/50 rounded-xl p-3 border border-white flex items-center gap-3" data-id="element-760" data-mp="3nog9wjkqvgww">
                  <Skeleton className="w-10 h-10 rounded-xl" data-id="element-761" data-mp="hmfyxgxlbx1a" />
                  <div className="flex-1" data-id="element-762" data-mp="hkwjfsjrdq0k">
                    <Skeleton className="h-4 w-32 mb-1" data-id="element-763" data-mp="conw3rveqqib" />
                    <Skeleton className="h-3 w-48" data-id="element-764" data-mp="cltf6ablrntn" />
                  </div>
                </div>)}
            </div> : loadError ? (/* AS-2026-07-P3-02: a failed load must not look like "no results yet". */
        <div className="text-center py-6" role="alert" data-id="element-765" data-mp="10cpahhf9p15n">
              <div className="text-4xl mb-2" data-id="element-766" data-mp="10fbjllht3opz">😕</div>
              <p className="text-slate-800 font-bold mb-1" data-id="element-767" data-mp="wb3qlc4bfmly">
                Resultaten konden niet geladen worden
              </p>
              <p className="text-sm text-slate-500 font-medium mb-3" data-id="element-768" data-mp="1a9k7fvvw6tht">
                Je oude toetsen zijn niet verdwenen — we konden ze even niet
                ophalen.
              </p>
              <Button onClick={() => window.location.reload()} variant="secondary" size="sm" data-id="element-769" data-mp="3c39qen0hyrln">
                Opnieuw proberen
              </Button>
            </div>) : !learnerId ? <div className="text-center py-6" data-id="element-770" data-mp="1rl8q7y1mq8g5">
              <div className="text-4xl mb-2" data-id="element-771" data-mp="1rn1vb8wlgkm1">👤</div>
              <p className="text-slate-800 font-bold mb-1" data-id="element-772" data-mp="1cwul1ggqq7wf">
                Geen leerling geselecteerd
              </p>
              <p className="text-sm text-slate-500 font-medium" data-id="element-773" data-mp="1htl329c3vdkh">
                Kies eerst een leerling om toetsresultaten te bekijken.
              </p>
            </div> : hasHistory ? <div className="space-y-2" data-id="element-774" data-mp="18w2eww360j4d">
              {displayedHistory.map((entry, index) => {
            const date = new Date(entry.completedAt);
            const dateStr = date.toLocaleDateString('nl-NL', {
              day: 'numeric',
              month: 'short'
            });
            const report = reports[entry.attemptId];
            const prevEntry = history.slice(index + 1).find(h => h.domainId === entry.domainId);
            const domainMeta = entry.domainId ? DOMAIN_META[entry.domainId] : null;
            const isAssigned = !!entry.assignmentId;
            // Score color
            const scoreBg = entry.score >= 70 ? 'bg-emerald-500' : entry.score >= 50 ? 'bg-amber-500' : 'bg-rose-500';
            // Trend
            let trendEl = null;
            if (prevEntry) {
              if (entry.score > prevEntry.score) {
                trendEl = <TrendingUp className="w-3.5 h-3.5 text-emerald-500" data-id="element-775" data-mp="ah0pyavkfkl3" />;
              } else if (entry.score < prevEntry.score) {
                trendEl = <TrendingDown className="w-3.5 h-3.5 text-rose-400" data-id="element-776" data-mp="2v784xvm1y4qz" />;
              }
            }
            return <div key={entry.attemptId} className="bg-white rounded-xl p-3 flex items-center gap-3 hover:shadow-sm transition-shadow border border-slate-100" data-id="element-777" data-mp="2sjny8ekv432g" data-mpi={index}>
                    {/* Score circle */}
                    <div className={`w-11 h-11 rounded-xl ${scoreBg} flex items-center justify-center flex-shrink-0`} data-id="element-778" data-mp="2wmat6octy43v">
                      <span className="text-white font-black text-sm" data-id="element-779" data-mp="28wzqh1bx5zn3">
                        {entry.score}%
                      </span>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0" data-id="element-780" data-mp="2inud46t2v192">
                      <div className="flex items-center gap-1.5" data-id="element-781" data-mp="2dvayr7mkg237">
                        <span className="text-sm font-bold text-slate-900" data-id="element-782" data-mp="2dtzwxdcol4eq">
                          {domainMeta ? domainMeta.shortLabel : 'Toets'}
                        </span>
                        {domainMeta && <span className={`w-4 h-4 rounded flex items-center justify-center ${domainMeta.bgColor} ${domainMeta.color}`} data-id="element-783" data-mp="3l9odzw3hsms0">
                            <domainMeta.icon className="w-2.5 h-2.5" data-id="element-784" data-mp="3uzget826qc4x" />
                          </span>}
                        {trendEl}
                        {isAssigned && <Badge variant="default" className="bg-indigo-100 text-indigo-700 border-indigo-200 text-[9px] px-1 py-0" data-id="element-785" data-mp="32asqf3m536n5">
                            Leerkracht
                          </Badge>}
                      </div>
                      <p className="text-[11px] text-slate-400 font-medium mt-0.5" data-id="element-786" data-mp="3gbvc3f982exj">
                        {entry.totalQuestions} vragen · {dateStr}
                      </p>
                    </div>

                    {/* Action */}
                    {report && !isAssigned ? <button onClick={() => onViewReport(report)} className="text-xs font-bold text-amber-600 hover:text-amber-700 flex items-center gap-1 flex-shrink-0" data-id="element-787" data-mp="2g12dnlhfu7yk">
                        Rapport <ArrowRight className="w-3 h-3" data-id="element-788" data-mp="22aqjlh1alz4s" />
                      </button> : isAssigned ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" data-id="element-789" data-mp="1xc5lbn5lppiz" /> : null}
                  </div>;
          })}

              {history.length > 5 && <button onClick={() => setShowAllResults(!showAllResults)} className="w-full text-center text-xs font-bold text-slate-500 hover:text-slate-700 py-2" data-id="element-790" data-mp="1obz904ln2vle">
                  {showAllResults ? 'Toon minder' : `Toon alle resultaten (${history.length})`}
                </button>}
            </div> : <div className="text-center py-6" data-id="element-791" data-mp="2afrl9wts33fj">
              <div className="text-4xl mb-2" data-id="element-792" data-mp="2fel0pusml4ic">🌟</div>
              <p className="text-slate-800 font-bold mb-1" data-id="element-793" data-mp="2fd9wjtv9puqp">
                Nog geen resultaten
              </p>
              <p className="text-sm text-slate-500 font-medium" data-id="element-794" data-mp="21gmoauxv7i5h">
                Start hierboven een toets om te zien waar je staat.
              </p>
            </div>}
        </Card>
      </motion.div>
    </PageContainer>;
}
function formatRemainingTime(ms: number): string {
  const minutes = Math.ceil(ms / (60 * 1000));
  if (minutes <= 1) return 'minder dan een minuut';
  if (minutes < 60) return `${minutes} minuten`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) return `${hours} uur`;
  return `${hours} uur en ${remainingMinutes} minuten`;
}